import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../Components/Footer';
import Auth from '../Function/Auth';
import Header from '../Components/Header';
import { toast, ToastContainer } from "react-toastify";
import { canModuleEdit } from '../Function/Permission';

// Helper to extract GPS Coordinates from text / user data
const parseGpsLocation = (locationStr) => {
    if (!locationStr || typeof locationStr !== 'string') return null;
    const str = locationStr.trim();
    if (!str) return null;

    // Pattern 1: "Latitude:25.4452761, Longitude:84.8592364"
    const latLngRegex = /Latitude:\s*([-\d.]+),\s*Longitude:\s*([-\d.]+)/i;
    const match = str.match(latLngRegex);
    if (match) {
        const lat = parseFloat(match[1]);
        const lng = parseFloat(match[2]);
        if (!isNaN(lat) && !isNaN(lng)) {
            return {
                isCoordinates: true,
                lat,
                lng,
                label: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
                routeDestination: `${lat},${lng}`
            };
        }
    }

    // Pattern 2: "28.6139, 77.2090"
    const commaSplit = str.split(',');
    if (commaSplit.length === 2 && !isNaN(parseFloat(commaSplit[0])) && !isNaN(parseFloat(commaSplit[1]))) {
        const lat = parseFloat(commaSplit[0].replace(/[^\d.-]/g, ''));
        const lng = parseFloat(commaSplit[1].replace(/[^\d.-]/g, ''));
        if (!isNaN(lat) && !isNaN(lng)) {
            return {
                isCoordinates: true,
                lat,
                lng,
                label: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
                routeDestination: `${lat},${lng}`
            };
        }
    }

    // Pattern 3: City or Address e.g. "India", "Gorakhpur"
    return {
        isCoordinates: false,
        lat: null,
        lng: null,
        label: str,
        routeDestination: str
    };
};

function Matching() {
    const authToken = sessionStorage.getItem('authToken');
    const rawBaseUrl = process.env.REACT_APP_BASE_API_URL || 'https://api.marclife.io/api/admin';
    const baseurl = rawBaseUrl.trim();

    // Primary Data State
    const [matchingList, setMatchingList] = useState([]);
    const [apiAnalytics, setApiAnalytics] = useState(null);
    const [tableLoading, setTableLoading] = useState(false);

    // Filter States
    const [searchTerm, setSearchTerm] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [serverPagination, setServerPagination] = useState(null);

    // Modals & Actions
    const [selectedItem, setSelectedItem] = useState(null);
    const [showDistributeModal, setShowDistributeModal] = useState(false);
    const [isDistributing, setIsDistributing] = useState(false);

    // Number formatting helper
    const formatNumber = (num, decimals = 2) => {
        if (num === null || num === undefined) return '0.00';
        if (typeof num === 'string') {
            num = parseFloat(num.replace(/[^\d.-]/g, ''));
        }
        return isNaN(num)
            ? '0.00'
            : num.toLocaleString('en-US', {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals
            });
    };

    // Copy helper
    const copyToClipboard = (text, label = 'Address') => {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            toast.success(`${label} copied to clipboard!`);
        }).catch(() => {
            toast.error('Failed to copy');
        });
    };

    const formatShortWallet = (wallet) => {
        if (!wallet) return 'N/A';
        if (wallet.length <= 16) return wallet;
        return `${wallet.slice(0, 8)}...${wallet.slice(-6)}`;
    };

    // Google Maps Turn-by-Turn GPS Directions Navigation
    const openDefineRoute = (destination) => {
        if (!destination) {
            toast.warn("Location unavailable for GPS routing");
            return;
        }
        const routeUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
        window.open(routeUrl, '_blank', 'noopener,noreferrer');
    };

    // Fetch Matching Bonus Data from Backend API
    const fetchMatchingData = useCallback(async (page = 1, perPage = itemsPerPage) => {
        setTableLoading(true);
        try {
            const response = await fetch(`${baseurl}/matching-list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({
                    page: page,
                    per_page: perPage,
                    search: searchTerm.trim() || undefined,
                    from_date: fromDate || undefined,
                    to_date: toDate || undefined,
                }),
            });

            if (response.status === 429) {
                toast.warn('Rate limit exceeded. Please wait a moment.');
                setTableLoading(false);
                return;
            }

            if (response.status === 401) {
                console.warn('API returned 401 Unauthenticated.');
                setTableLoading(false);
                return;
            }

            const result = await response.json();

            if (result && result.status) {
                setMatchingList(result.data || []);
                if (result.analytics) {
                    setApiAnalytics(result.analytics);
                }
                if (result.pagination) {
                    setServerPagination(result.pagination);
                }
            } else {
                setMatchingList([]);
            }
        } catch (error) {
            console.error('Fetch matching error:', error);
            toast.error('Failed to load matching bonus records');
        } finally {
            setTableLoading(false);
        }
    }, [authToken, baseurl, itemsPerPage, searchTerm, fromDate, toDate]);

    useEffect(() => {
        fetchMatchingData(1, itemsPerPage);
    }, [fetchMatchingData, itemsPerPage]);

    // Handle Manual Trigger of Matching Distribution
    const handleDistributeMatching = async () => {
        setIsDistributing(true);
        try {
            const response = await fetch(`${baseurl}/matching-distribut`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    Authorization: `Bearer ${authToken}`,
                },
            });

            const data = await response.json();
            if (data && data.status) {
                toast.success(data.message || 'Matching distribution processed successfully!');
                setShowDistributeModal(false);
                fetchMatchingData(1, itemsPerPage);
            } else {
                toast.error(data?.message || 'Failed to process matching distribution');
            }
        } catch (err) {
            console.error('Distribute matching error:', err);
            toast.error('Network error during matching distribution');
        } finally {
            setIsDistributing(false);
        }
    };

    // Client-side filtering as fallback
    const filteredList = useMemo(() => {
        if (serverPagination && Number(serverPagination.total) > 0) {
            return matchingList;
        }

        let list = [...matchingList];
        if (searchTerm.trim()) {
            const q = searchTerm.trim().toLowerCase();
            list = list.filter(item => {
                const uName = (item.user_name || item.user?.name || '').toLowerCase();
                const uId = String(item.user_id || item.user?.user_id || '').toLowerCase();
                const spId = String(item.sponsor_id || item.user?.sponsor_id || '').toLowerCase();
                const w = (item.wallet || item.user?.wallet || '').toLowerCase();
                return uName.includes(q) || uId.includes(q) || spId.includes(q) || w.includes(q);
            });
        }
        return list;
    }, [matchingList, searchTerm, serverPagination]);

    // Computed Analytics
    const analytics = useMemo(() => {
        if (apiAnalytics) {
            return {
                total_matching: Number(apiAnalytics.total_matching || 0),
                total_matching_business: parseFloat(apiAnalytics.total_matching_business || 0),
                total_credit_reward: parseFloat(apiAnalytics.total_credit_reward || 0),
                total_coin_amount: parseFloat(apiAnalytics.total_coin_amount || 0),
                total_strong_business: parseFloat(apiAnalytics.total_strong_business || 0),
                total_weak_business: parseFloat(apiAnalytics.total_weak_business || 0),
            };
        }

        let tCount = matchingList.length;
        let tM = 0;
        let tR = 0;
        let tC = 0;
        let tS = 0;
        let tW = 0;

        matchingList.forEach(item => {
            tM += parseFloat(item.matching_business) || 0;
            tR += parseFloat(item.credit_reward) || 0;
            tC += parseFloat(item.coin_amount) || 0;
            tS += parseFloat(item.strong_business) || 0;
            tW += parseFloat(item.weak_business) || 0;
        });

        return {
            total_matching: tCount,
            total_matching_business: tM,
            total_credit_reward: tR,
            total_coin_amount: tC,
            total_strong_business: tS,
            total_weak_business: tW,
        };
    }, [apiAnalytics, matchingList]);

    // Pagination calculations
    const isServerPaginated = Boolean(serverPagination && Number(serverPagination.total) > 0);
    const totalCount = isServerPaginated ? Number(serverPagination.total) : filteredList.length;
    const totalPages = isServerPaginated
        ? (Number(serverPagination.last_page) || Math.ceil(totalCount / itemsPerPage) || 1)
        : (Math.ceil(filteredList.length / itemsPerPage) || 1);

    const paginatedList = useMemo(() => {
        if (isServerPaginated) return filteredList;
        const start = (currentPage - 1) * itemsPerPage;
        return filteredList.slice(start, start + itemsPerPage);
    }, [filteredList, currentPage, itemsPerPage, isServerPaginated]);

    const handlePageChange = (p) => {
        if (p >= 1 && p <= totalPages && p !== currentPage) {
            setCurrentPage(p);
            fetchMatchingData(p, itemsPerPage);
        }
    };

    const handleItemsPerPageChange = (size) => {
        setItemsPerPage(size);
        setCurrentPage(1);
        fetchMatchingData(1, size);
    };

    const handleResetFilters = () => {
        setSearchTerm('');
        setFromDate('');
        setToDate('');
        setCurrentPage(1);
        fetchMatchingData(1, itemsPerPage);
        toast.info('Filters reset');
    };

    // Location resolver
    const getItemLocationInfo = (item) => {
        const rawLoc = item.user_location || item.user?.location || item.user?.login_location || item.user?.fix_address;
        return parseGpsLocation(rawLoc);
    };

    return (
        <>
            <Header>
                <Auth />
                <div className="main-content app-content">
                    <div className="container-fluid">

                        {/* Page Title & Breadcrumb */}
                        <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
                            <div>
                                <h4 className="mb-0 text-dark fw-bold">Matching Bonus Management</h4>
                                <p className="mb-0 text-muted fs-13">
                                    Monitor binary matching businesses, rewards in USDT & NMRC coins, and weekly distribution history.
                                </p>
                            </div>
                            <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
                                {canModuleEdit('matching') ? (
                                    <button
                                        onClick={() => setShowDistributeModal(true)}
                                        className="btn btn-primary btn-wave d-inline-flex align-items-center gap-2"
                                        title="Calculate and Distribute Matching Bonus"
                                    >
                                        <i className="fa fa-calculator"></i>
                                        <span>Distribute Matching</span>
                                    </button>
                                ) : (
                                    <span className="badge bg-secondary-transparent text-secondary px-2 py-1 fs-12 d-inline-flex align-items-center gap-1">
                                        <i className="fa fa-eye"></i> Viewer Mode
                                    </span>
                                )}
                                <button
                                    onClick={() => fetchMatchingData(currentPage, itemsPerPage)}
                                    className="btn btn-outline-primary btn-wave d-inline-flex align-items-center gap-2"
                                    disabled={tableLoading}
                                >
                                    <i className={`fa fa-refresh ${tableLoading ? 'fa-spin' : ''}`}></i>
                                    <span>Refresh</span>
                                </button>
                            </div>
                        </div>

                        {/* Top KPI Analytics Summary Cards */}
                        <div className="row g-3 mb-4">
                            {/* Total Matching Distributions */}
                            <div className="col-xl-3 col-lg-6 col-md-6 col-12">
                                <div className="card overflow-hidden sales-card bg-primary-gradient border-0 shadow-sm h-100">
                                    <div className="p-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <h6 className="mb-2 fs-12 text-fixed-white text-uppercase fw-semibold op-8">
                                                Total Distributions
                                            </h6>
                                            <span className="badge bg-white-2 text-fixed-white rounded-pill px-2 py-1 fs-11">
                                                All Time
                                            </span>
                                        </div>
                                        <div className="d-flex align-items-baseline justify-content-between mt-2">
                                            <h3 className="fs-24 fw-bold mb-0 text-fixed-white">
                                                {analytics.total_matching.toLocaleString()}
                                            </h3>
                                            <div className="text-end">
                                                <span className="fs-12 text-fixed-white op-8 d-block">Matching Volume</span>
                                                <span className="fs-14 fw-bold text-fixed-white">
                                                    ${formatNumber(analytics.total_matching_business)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Total Credit Reward (USDT) */}
                            <div className="col-xl-3 col-lg-6 col-md-6 col-12">
                                <div className="card overflow-hidden sales-card bg-success-gradient border-0 shadow-sm h-100">
                                    <div className="p-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <h6 className="mb-2 fs-12 text-fixed-white text-uppercase fw-semibold op-8">
                                                Total Credit Reward
                                            </h6>
                                            <span className="badge bg-white-2 text-fixed-white rounded-pill px-2 py-1 fs-11">
                                                USDT Value
                                            </span>
                                        </div>
                                        <div className="d-flex align-items-baseline justify-content-between mt-2">
                                            <h3 className="fs-24 fw-bold mb-0 text-fixed-white">
                                                ${formatNumber(analytics.total_credit_reward)}
                                            </h3>
                                            <div className="text-end">
                                                <span className="fs-12 text-fixed-white op-8 d-block">Reward %</span>
                                                <span className="fs-14 fw-bold text-fixed-white">
                                                    Standard 10%
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Total NMRC Distributed */}
                            <div className="col-xl-3 col-lg-6 col-md-6 col-12">
                                <div className="card overflow-hidden sales-card bg-warning-gradient border-0 shadow-sm h-100">
                                    <div className="p-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <h6 className="mb-2 fs-12 text-fixed-white text-uppercase fw-semibold op-8">
                                                NMRC Distributed
                                            </h6>
                                            <span className="badge bg-white-2 text-fixed-white rounded-pill px-2 py-1 fs-11">
                                                Coin Volume
                                            </span>
                                        </div>
                                        <div className="d-flex align-items-baseline justify-content-between mt-2">
                                            <h3 className="fs-24 fw-bold mb-0 text-fixed-white">
                                                {formatNumber(analytics.total_coin_amount, 4)}
                                            </h3>
                                            <div className="text-end">
                                                <span className="fs-12 text-fixed-white op-8 d-block">Asset</span>
                                                <span className="fs-14 fw-bold text-fixed-white">
                                                    NMRC Token
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Leg Business Balance */}
                            <div className="col-xl-3 col-lg-6 col-md-6 col-12">
                                <div className="card overflow-hidden sales-card bg-danger-gradient border-0 shadow-sm h-100">
                                    <div className="p-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <h6 className="mb-2 fs-12 text-fixed-white text-uppercase fw-semibold op-8">
                                                Leg Business Totals
                                            </h6>
                                            <span className="badge bg-white-2 text-fixed-white rounded-pill px-2 py-1 fs-11">
                                                Strong vs Weak
                                            </span>
                                        </div>
                                        <div className="d-flex align-items-baseline justify-content-between mt-2">
                                            <div>
                                                <span className="fs-11 text-fixed-white op-8 d-block">Strong Leg</span>
                                                <h4 className="fs-18 fw-bold mb-0 text-fixed-white">
                                                    ${formatNumber(analytics.total_strong_business)}
                                                </h4>
                                            </div>
                                            <div className="text-end">
                                                <span className="fs-11 text-fixed-white op-8 d-block">Weak Leg</span>
                                                <h4 className="fs-18 fw-bold mb-0 text-fixed-white">
                                                    ${formatNumber(analytics.total_weak_business)}
                                                </h4>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Filter and Search Card */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="card shadow-sm border-0">
                                    <div className="card-body p-3">
                                        <div className="row g-3 align-items-end">
                                            {/* Search Bar */}
                                            <div className="col-xl-5 col-lg-5 col-md-6 col-12">
                                                <label className="form-label fs-12 text-muted mb-1 fw-semibold">
                                                    Quick Search
                                                </label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light border-end-0">
                                                        <i className="fa fa-search text-muted"></i>
                                                    </span>
                                                    <input
                                                        type="text"
                                                        value={searchTerm}
                                                        onChange={(e) => {
                                                            setSearchTerm(e.target.value);
                                                            setCurrentPage(1);
                                                        }}
                                                        className="form-control border-start-0"
                                                        placeholder="Search user name, ID, sponsor ID, or wallet..."
                                                    />
                                                    {searchTerm && (
                                                        <button
                                                            className="btn btn-outline-secondary"
                                                            type="button"
                                                            onClick={() => setSearchTerm('')}
                                                        >
                                                            <i className="fa fa-times"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {/* From Date */}
                                            <div className="col-xl-2 col-lg-2 col-md-6 col-6">
                                                <label className="form-label fs-12 text-muted mb-1 fw-semibold">
                                                    From Date
                                                </label>
                                                <input
                                                    type="date"
                                                    value={fromDate}
                                                    onChange={(e) => {
                                                        setFromDate(e.target.value);
                                                        setCurrentPage(1);
                                                    }}
                                                    className="form-control"
                                                />
                                            </div>

                                            {/* To Date */}
                                            <div className="col-xl-2 col-lg-2 col-md-6 col-6">
                                                <label className="form-label fs-12 text-muted mb-1 fw-semibold">
                                                    To Date
                                                </label>
                                                <input
                                                    type="date"
                                                    value={toDate}
                                                    onChange={(e) => {
                                                        setToDate(e.target.value);
                                                        setCurrentPage(1);
                                                    }}
                                                    className="form-control"
                                                />
                                            </div>

                                            {/* Search Button */}
                                            <div className="col-xl-1 col-lg-1 col-md-6 col-6 text-end">
                                                <button
                                                    onClick={() => fetchMatchingData(1, itemsPerPage)}
                                                    className="btn btn-primary w-100"
                                                    title="Apply Filters"
                                                >
                                                    <i className="fa fa-filter me-1"></i> Filter
                                                </button>
                                            </div>

                                            {/* Reset Button */}
                                            <div className="col-xl-2 col-lg-2 col-md-6 col-6 text-end">
                                                <button
                                                    onClick={handleResetFilters}
                                                    className="btn btn-light w-100 border text-muted"
                                                    title="Reset Filters"
                                                >
                                                    <i className="fa fa-undo me-1"></i> Reset
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Matching Bonus Table Card */}
                        <div className="row">
                            <div className="col-12">
                                <div className="card shadow-sm border-0">
                                    <div className="card-header d-flex justify-content-between align-items-center bg-transparent py-3 border-bottom">
                                        <div className="d-flex align-items-center gap-2">
                                            <h5 className="card-title mb-0 fw-bold text-dark">
                                                Matching Bonus History
                                            </h5>
                                            <span className="badge bg-primary-transparent text-primary rounded-pill">
                                                {totalCount} Records
                                            </span>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="d-flex align-items-center gap-1 fs-12 text-muted">
                                                <span>Show:</span>
                                                <select
                                                    className="form-select form-select-sm py-1 px-2"
                                                    style={{ width: '75px' }}
                                                    value={itemsPerPage}
                                                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                                                >
                                                    <option value={10}>10</option>
                                                    <option value={25}>25</option>
                                                    <option value={50}>50</option>
                                                    <option value={100}>100</option>
                                                </select>
                                            </div>
                                            <span className="text-muted fs-12 d-none d-sm-inline">
                                                Showing {paginatedList.length} of {totalCount} entries
                                            </span>
                                        </div>
                                    </div>

                                    <div className="card-body p-0">
                                        <div className="table-responsive country-table">
                                            {tableLoading ? (
                                                <div className="text-center py-5">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    <p className="mt-2 text-muted fs-13">Fetching matching bonus records...</p>
                                                </div>
                                            ) : (
                                                <table className="table table-hover align-middle mb-0 text-nowrap">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th scope="col" className="ps-3">ID</th>
                                                            <th scope="col">CLIENT</th>
                                                            <th scope="col">WALLET</th>
                                                            <th scope="col">STRONG BUSINESS</th>
                                                            <th scope="col">WEAK BUSINESS</th>
                                                            <th scope="col">MATCHING BUSINESS</th>
                                                            <th scope="col">REWARD %</th>
                                                            <th scope="col">CREDIT REWARD ($)</th>
                                                            <th scope="col">NMRC AMOUNT</th>
                                                            <th scope="col">DATE & TIME</th>
                                                            <th scope="col" className="text-center pe-3">ACTIONS</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {paginatedList.length > 0 ? (
                                                            paginatedList.map((item) => {
                                                                const locationInfo = getItemLocationInfo(item);
                                                                const walletAddr = item.wallet || item.user?.wallet;

                                                                return (
                                                                    <tr key={item.id}>
                                                                        {/* Record ID */}
                                                                        <td className="ps-3 text-muted fw-semibold fs-12">
                                                                            #{item.id}
                                                                        </td>

                                                                        {/* Client info */}
                                                                        <td>
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <img
                                                                                    src={item.profile_image || item.user?.profile_image || "https://apiuser.ride2luxury.in/images/profileImage/avtarImage-1.avif"}
                                                                                    alt={item.user_name || item.user?.name || "User"}
                                                                                    className="rounded-circle avatar-sm border"
                                                                                    style={{ width: "38px", height: "38px", objectFit: "cover" }}
                                                                                    onError={(e) => {
                                                                                        e.target.onerror = null;
                                                                                        e.target.src = "https://apiuser.ride2luxury.in/images/profileImage/avtarImage-1.avif";
                                                                                    }}
                                                                                />
                                                                                <div>
                                                                                    <Link
                                                                                        to="/clientsDetails"
                                                                                        onClick={() => sessionStorage.setItem("clientId", item.user_id || item.user?.user_id)}
                                                                                        className="text-primary fw-bold text-decoration-none d-block lh-1 hover-underline"
                                                                                        title="View Client Details"
                                                                                    >
                                                                                        {item.user_name || item.user?.name || 'Unnamed Client'}
                                                                                    </Link>
                                                                                    <small className="text-muted fs-11">
                                                                                        ID: <span className="fw-semibold">#{item.user_id || item.user?.user_id || 'N/A'}</span>
                                                                                        {item.sponsor_id && (
                                                                                            <span className="ms-1 op-7">| Sp: #{item.sponsor_id}</span>
                                                                                        )}
                                                                                    </small>
                                                                                </div>
                                                                            </div>
                                                                        </td>

                                                                        {/* Wallet Address */}
                                                                        <td>
                                                                            <div className="d-flex align-items-center gap-1 font-monospace fs-12">
                                                                                <span title={walletAddr}>
                                                                                    {formatShortWallet(walletAddr)}
                                                                                </span>
                                                                                {walletAddr && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => copyToClipboard(walletAddr, 'Wallet Address')}
                                                                                        className="btn btn-link btn-sm p-0 text-muted ms-1"
                                                                                        title="Copy Wallet Address"
                                                                                    >
                                                                                        <i className="fa fa-copy"></i>
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </td>

                                                                        {/* Strong Business */}
                                                                        <td>
                                                                            <span className="fw-semibold text-dark fs-13">
                                                                                ${formatNumber(item.strong_business)}
                                                                            </span>
                                                                        </td>

                                                                        {/* Weak Business */}
                                                                        <td>
                                                                            <span className="fw-semibold text-dark fs-13">
                                                                                ${formatNumber(item.weak_business)}
                                                                            </span>
                                                                        </td>

                                                                        {/* Matching Business */}
                                                                        <td>
                                                                            <span className="badge bg-primary-transparent text-primary fw-bold fs-12 px-2 py-1">
                                                                                ${formatNumber(item.matching_business)}
                                                                            </span>
                                                                        </td>

                                                                        {/* Reward % */}
                                                                        <td>
                                                                            <span className="badge bg-info-transparent text-info fw-semibold">
                                                                                {item.reward_percentage || 10}%
                                                                            </span>
                                                                        </td>

                                                                        {/* Credit Reward in USD */}
                                                                        <td>
                                                                            <span className="fw-bold text-success fs-13">
                                                                                +${formatNumber(item.credit_reward)}
                                                                            </span>
                                                                        </td>

                                                                        {/* Coin Amount (NMRC) */}
                                                                        <td>
                                                                            <div className="fw-bold text-dark fs-12">
                                                                                {formatNumber(item.coin_amount, 4)} NMRC
                                                                            </div>
                                                                            <small className="text-muted fs-11">
                                                                                @ ${parseFloat(item.coin_price || 0).toFixed(4)}
                                                                            </small>
                                                                        </td>



                                                                        {/* Date Time */}
                                                                        <td className="fs-12 text-muted">
                                                                            {item.date_time || item.created_at || 'N/A'}
                                                                        </td>

                                                                        {/* Actions */}
                                                                        <td className="text-center pe-3">
                                                                            <button
                                                                                onClick={() => setSelectedItem(item)}
                                                                                className="btn btn-sm btn-outline-primary"
                                                                                title="View Matching Details"
                                                                            >
                                                                                <i className="fa fa-eye me-1"></i> Details
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })
                                                        ) : (
                                                            <tr>
                                                                <td colSpan={12} className="text-center py-5">
                                                                    <div className="py-4">
                                                                        <i className="fa fa-folder-open-o fs-40 text-muted op-5 mb-2 d-block"></i>
                                                                        <h6 className="text-muted fw-semibold">No Matching Bonus Records Found</h6>
                                                                        <p className="text-muted fs-12 mb-0">Try adjusting your search criteria or date filters.</p>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>

                                        {/* Pagination Footer */}
                                        <div className="d-flex flex-wrap justify-content-between align-items-center p-3 border-top">
                                            <div className="fs-12 text-muted mb-2 mb-md-0">
                                                Page <b>{currentPage}</b> of <b>{totalPages}</b> (Total {totalCount} records)
                                            </div>
                                            <div className="d-flex align-items-center gap-1">
                                                <button
                                                    onClick={() => handlePageChange(1)}
                                                    disabled={currentPage <= 1}
                                                    className="btn btn-sm btn-light border"
                                                    title="First Page"
                                                >
                                                    <i className="fa fa-angle-double-left"></i>
                                                </button>
                                                <button
                                                    onClick={() => handlePageChange(currentPage - 1)}
                                                    disabled={currentPage <= 1}
                                                    className="btn btn-sm btn-light border"
                                                    title="Previous Page"
                                                >
                                                    <i className="fa fa-angle-left"></i> Prev
                                                </button>

                                                {/* Page Numbers */}
                                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                    let pageNum;
                                                    if (totalPages <= 5) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage >= totalPages - 2) {
                                                        pageNum = totalPages - 4 + i;
                                                    } else {
                                                        pageNum = currentPage - 2 + i;
                                                    }

                                                    return (
                                                        <button
                                                            key={pageNum}
                                                            onClick={() => handlePageChange(pageNum)}
                                                            className={`btn btn-sm ${currentPage === pageNum ? 'btn-primary' : 'btn-light border'}`}
                                                        >
                                                            {pageNum}
                                                        </button>
                                                    );
                                                })}

                                                <button
                                                    onClick={() => handlePageChange(currentPage + 1)}
                                                    disabled={currentPage >= totalPages}
                                                    className="btn btn-sm btn-light border"
                                                    title="Next Page"
                                                >
                                                    Next <i className="fa fa-angle-right"></i>
                                                </button>
                                                <button
                                                    onClick={() => handlePageChange(totalPages)}
                                                    disabled={currentPage >= totalPages}
                                                    className="btn btn-sm btn-light border"
                                                    title="Last Page"
                                                >
                                                    <i className="fa fa-angle-double-right"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* Details Modal */}
                {selectedItem && (
                    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content shadow-lg border-0">
                                <div className="modal-header bg-primary text-white py-3">
                                    <h5 className="modal-title fs-16 fw-bold">
                                        <i className="fa fa-info-circle me-2"></i>
                                        Matching Bonus Details #{selectedItem.id}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() => setSelectedItem(null)}
                                    ></button>
                                </div>
                                <div className="modal-body p-4">
                                    {/* Client info banner */}
                                    <div className="d-flex align-items-center gap-3 p-3 bg-light rounded mb-4">
                                        <img
                                            src={selectedItem.profile_image || selectedItem.user?.profile_image || "https://apiuser.ride2luxury.in/images/profileImage/avtarImage-1.avif"}
                                            alt={selectedItem.user_name || "User"}
                                            className="rounded-circle border"
                                            style={{ width: "55px", height: "55px", objectFit: "cover" }}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = "https://apiuser.ride2luxury.in/images/profileImage/avtarImage-1.avif";
                                            }}
                                        />
                                        <div>
                                            <h5 className="mb-1 fw-bold text-dark">{selectedItem.user_name || selectedItem.user?.name || 'Unnamed Client'}</h5>
                                            <p className="mb-0 text-muted fs-12">
                                                User ID: <span className="fw-semibold text-dark">#{selectedItem.user_id}</span> |
                                                Sponsor ID: <span className="fw-semibold text-dark">#{selectedItem.sponsor_id}</span>
                                            </p>
                                            <div className="d-flex align-items-center gap-1 font-monospace fs-11 text-muted mt-1">
                                                <span>Wallet: {selectedItem.wallet || selectedItem.user?.wallet || 'N/A'}</span>
                                                {(selectedItem.wallet || selectedItem.user?.wallet) && (
                                                    <button
                                                        type="button"
                                                        onClick={() => copyToClipboard(selectedItem.wallet || selectedItem.user?.wallet, 'Wallet Address')}
                                                        className="btn btn-link btn-sm p-0 text-muted"
                                                    >
                                                        <i className="fa fa-copy"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Financial Breakdown Grid */}
                                    <div className="row g-3 mb-3">
                                        <div className="col-md-4 col-sm-6">
                                            <div className="p-3 border rounded bg-light-subtle">
                                                <small className="text-muted d-block mb-1">Strong Business</small>
                                                <h5 className="mb-0 fw-bold text-dark">${formatNumber(selectedItem.strong_business)}</h5>
                                            </div>
                                        </div>
                                        <div className="col-md-4 col-sm-6">
                                            <div className="p-3 border rounded bg-light-subtle">
                                                <small className="text-muted d-block mb-1">Weak Business</small>
                                                <h5 className="mb-0 fw-bold text-dark">${formatNumber(selectedItem.weak_business)}</h5>
                                            </div>
                                        </div>
                                        <div className="col-md-4 col-sm-6">
                                            <div className="p-3 border rounded bg-primary-transparent">
                                                <small className="text-primary d-block mb-1 fw-semibold">Matching Business</small>
                                                <h5 className="mb-0 fw-bold text-primary">${formatNumber(selectedItem.matching_business)}</h5>
                                            </div>
                                        </div>
                                        <div className="col-md-4 col-sm-6">
                                            <div className="p-3 border rounded bg-light-subtle">
                                                <small className="text-muted d-block mb-1">Reward Percentage</small>
                                                <h5 className="mb-0 fw-bold text-info">{selectedItem.reward_percentage || 10}%</h5>
                                            </div>
                                        </div>
                                        <div className="col-md-4 col-sm-6">
                                            <div className="p-3 border rounded bg-success-transparent">
                                                <small className="text-success d-block mb-1 fw-semibold">Credit Reward (USDT)</small>
                                                <h5 className="mb-0 fw-bold text-success">+${formatNumber(selectedItem.credit_reward)}</h5>
                                            </div>
                                        </div>
                                        <div className="col-md-4 col-sm-6">
                                            <div className="p-3 border rounded bg-warning-transparent">
                                                <small className="text-warning-emphasis d-block mb-1 fw-semibold">Coin Amount (NMRC)</small>
                                                <h5 className="mb-0 fw-bold text-dark">{formatNumber(selectedItem.coin_amount, 4)} NMRC</h5>
                                                <small className="text-muted fs-11">@ ${parseFloat(selectedItem.coin_price || 0).toFixed(4)}</small>
                                            </div>
                                        </div>
                                    </div>


                                </div>
                                <div className="modal-footer bg-light py-2">
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => setSelectedItem(null)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Distribute Confirmation Modal */}
                {showDistributeModal && (
                    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content shadow-lg border-0">
                                <div className="modal-header bg-primary text-white py-3">
                                    <h5 className="modal-title fs-16 fw-bold">
                                        <i className="fa fa-calculator me-2"></i>
                                        Process Matching Bonus Distribution
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() => setShowDistributeModal(false)}
                                        disabled={isDistributing}
                                    ></button>
                                </div>
                                <div className="modal-body p-4 text-center">
                                    <div className="mb-3">
                                        <i className="fa fa-exclamation-circle text-primary fs-50"></i>
                                    </div>
                                    <h5 className="fw-bold mb-2">Calculate & Distribute Matching Business?</h5>
                                    <p className="text-muted fs-13 mb-0">
                                        This action will calculate strong leg business, weak leg business, and matching business for all eligible active staked users, and credit their matching rewards into their wallets.
                                    </p>
                                </div>
                                <div className="modal-footer bg-light py-2">
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => setShowDistributeModal(false)}
                                        disabled={isDistributing}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                                        onClick={handleDistributeMatching}
                                        disabled={isDistributing}
                                    >
                                        {isDistributing && <i className="fa fa-spinner fa-spin"></i>}
                                        <span>Confirm & Distribute</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <ToastContainer />
            </Header>
            <Footer />
        </>
    );
}

export default Matching;